import * as Sentry from "@sentry/react";

import { supabase } from "@/integrations/supabase/client";

/**
 * Inicializa o Sentry no browser. Opt-in: sem `VITE_SENTRY_DSN` vira no-op.
 *
 * O que isso resolve: até aqui, erro de JS no app do cliente era invisível.
 * `reportError.ts` cobre 9 catch blocks escritos à mão; tudo que escapa deles
 * (crash de render, promise rejeitada, erro em event handler) sumia. O caso
 * dos 9 alertas `Load failed` sem contexto no docs/ISSUES.md nasce daí.
 *
 * O `reportError.ts` e a tabela `client_errors` CONTINUAM — eles alimentam o
 * alerta de Telegram. Sentry entra como camada de profundidade (stack trace,
 * breadcrumbs, release), não como substituto.
 *
 * ⚠️ LGPD — este app carrega dado de saúde. Três decisões deliberadas:
 *   1. Session Replay DESLIGADO. Gravar a tela aqui é dado de saúde em vídeo.
 *   2. `sendDefaultPii: false` — sem IP, sem cookie, sem corpo de request.
 *   3. `beforeSend` mascara email, CPF e telefone, inspirado no
 *      `sentry_scrub_pii` de backend/execution/observability.py — mas indo
 *      ALÉM dele: aqui `contexts` também é varrido. O do backend não varre, e
 *      é onde a `extraErrorDataIntegration` despeja os campos do Error.
 *
 * UUID é mascarado no browser. O trace do P0 mostrou que logs da bridge nativa
 * carregavam identificadores de usuário; performance precisa de duração e
 * outcome, não de identidade.
 */

const PII_PATTERNS: Array<[RegExp, string]> = [
  [/[\w.+-]+@[\w-]+\.[\w.-]+/g, "[email]"],
  [/\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g, "[cpf]"],
  // ⚠️ A borda NÃO pode ser `\b`: não existe boundary entre um espaço e `(`,
  // nem entre um espaço e `+`. O match começava DEPOIS do prefixo, e
  // `(11) 99999-8888` virava `([tel]` — `+55 11 99999-8888` virava `+[tel]`.
  // Um parêntese ou um `+` órfão ao lado do marcador denuncia o formato e,
  // pior, prova que havia um telefone ali. Medido em 2026-09-03.
  //
  // ⚠️ Mas a borda também NÃO pode ser `[^\d]`, que foi a primeira correção e
  // introduziu um dano PRÓPRIO: `[^\d]` casa depois de LETRA, e este padrão
  // roda ANTES do de UUID. Medido em 200.000 sorteios: 0,749% dos UUID v4
  // deixavam de virar `[uuid]` (`…-bf6174257015` → `…-bf[tel]`), e 0,36% dos
  // `trace_id` de 32 hex eram alterados. Com o `\b` original isso era 0,008% e
  // 0,000% — ou seja, comer id NUNCA foi propriedade do padrão original; era
  // regressão da borda nova. Excluir letra e dígito dos dois lados zera o dano.
  //
  // Lookbehind resolveria em uma linha, mas o app roda em WKWebView via Median
  // e o Safari só ganhou lookbehind no 16.4 — um SyntaxError neste módulo
  // derrubaria o app inteiro no aparelho antigo. Daí a borda por grupo + `$1`,
  // que preserva o caractere anterior (espaço, `/`, `:`), e o `(?![0-9A-Za-z])`
  // no fim, que impede cortar um id no meio.
  //
  // `(` e `+` ficam FORA da classe da borda de propósito: são do próprio
  // telefone, e o padrão precisa consumi-los para não deixar órfão.
  //
  // O `\s*` que separa país de DDD vive DENTRO do grupo opcional de propósito:
  // solto, ele comia o espaço antes do `(` e `ligar (11) …` virava `ligar[tel]`.
  [/(^|[^0-9A-Za-z(+])((?:\+?55\s*)?\(?\d{2}\)?\s*9?\d{4}-?\d{4})(?![0-9A-Za-z])/g, "$1[tel]"],
  [/\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi, "[uuid]"],
];

/**
 * Mascara PII num texto livre. **Exportada para teste**: é o único ponto do
 * arquivo exercitável sem subir o SDK, e `beforeSend`, `beforeSendSpan` e
 * `beforeSendLog` passam todos por ela. Comportamento idêntico ao de antes da
 * exportação — só o nome mudou (`scrub` → `scrubTexto`).
 */
export function scrubTexto(texto: string): string {
  return PII_PATTERNS.reduce((acc, [re, sub]) => acc.replace(re, sub), texto);
}

function scrubUnknown(value: unknown): unknown {
  if (typeof value === "string") return scrubTexto(value);
  if (Array.isArray(value)) return value.map(scrubUnknown);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [key, scrubUnknown(item)]),
    );
  }
  return value;
}

/**
 * Percorre o evento mascarando PII nos campos que carregam texto livre.
 * Exportada para teste — é o `beforeSend` de fato.
 */
export function scrubEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  for (const ex of event.exception?.values ?? []) {
    if (ex.value) ex.value = scrubTexto(ex.value);
  }
  if (event.message) event.message = scrubTexto(event.message);
  for (const crumb of event.breadcrumbs ?? []) {
    if (crumb.message) crumb.message = scrubTexto(crumb.message);
    if (crumb.data) crumb.data = scrubUnknown(crumb.data) as Record<string, unknown>;
  }
  // A URL pode carregar dado em query string.
  if (event.request?.url) event.request.url = scrubTexto(event.request.url);
  // `extra` carrega texto livre que o caller escolheu.
  if (event.extra) {
    for (const [k, v] of Object.entries(event.extra)) {
      event.extra[k] = scrubUnknown(v);
    }
  }
  // ⚠️ `contexts` TAMBÉM passa por aqui — o backend (`sentry_scrub_pii` em
  // observability.py) varre só `extra`, e copiar essa omissão aqui deixava um
  // buraco real: `extraErrorDataIntegration()` (ligada logo abaixo) grava as
  // propriedades próprias do Error em `contexts[<nome da exceção>]`. Um
  // `PostgrestError` chega com `details` e `hint`, que é onde o Postgres
  // devolve o VALOR que violou a constraint — email, CPF, telefone. O evento
  // saía com `exception.value` mascarado e o dado limpo logo ao lado.
  //
  // Do `trace`, só os IDENTIFICADORES ficam de fora do scrub — `trace_id`,
  // `span_id` e `parent_span_id`. Com a borda alfanumérica do padrão de
  // telefone eles já não corriam risco, mas a exceção segue como cinto e
  // suspensório: mascarar id de correlação quebraria a ligação entre spans sem
  // erro nenhum aparecendo. O RESTO do `trace` é varrido — `trace.data` carrega
  // URL com query string, e uma exceção larga demais aqui recriaria o buraco
  // que este bloco existe para fechar.
  if (event.contexts) {
    const { trace, ...resto } = event.contexts;
    const restoLimpo = scrubUnknown(resto) as typeof event.contexts;
    if (trace) {
      const { trace_id, span_id, parent_span_id, ...traceResto } = trace as Record<
        string,
        unknown
      >;
      restoLimpo.trace = {
        ...(scrubUnknown(traceResto) as Record<string, unknown>),
        ...(trace_id !== undefined ? { trace_id } : {}),
        ...(span_id !== undefined ? { span_id } : {}),
        ...(parent_span_id !== undefined ? { parent_span_id } : {}),
      } as typeof trace;
    }
    event.contexts = restoLimpo;
  }
  if (event.tags) {
    for (const [k, v] of Object.entries(event.tags)) event.tags[k] = scrubTexto(String(v));
  }
  if (event.user) event.user = scrubUnknown(event.user) as typeof event.user;
  return event;
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return; // dev, ou variável não passada no build — segue sem Sentry
  const release = import.meta.env.VITE_SENTRY_RELEASE?.trim() || undefined;
  const dist = import.meta.env.VITE_SENTRY_DIST?.trim() || undefined;

  try {
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE === "production" ? "production" : "development",
      release,
      dist,

      // Sem IP, sem cookie, sem corpo de request. Padrão do SDK é true.
      sendDefaultPii: false,

      integrations: [
        // Sem esta integração, traces existiam sem spans. Mantemos a
        // instrumentação local (sem tracePropagationTargets cross-origin) para
        // não mudar CORS do backend protegido nesta correção.
        Sentry.browserTracingIntegration({
          beforeStartSpan: (context) => ({ ...context, name: scrubTexto(context.name) }),
        }),

        // Supabase — o app lê dieta, perfil e histórico DIRETO do Supabase
        // (regra do AGENTS.md: leitura não passa pelo backend). Então erro de
        // RLS, RPC ou query nunca aparecia em lugar nenhum: não é log do
        // backend, e o `catch` do hook engolia.
        //
        // ⚠️ `sendOperationData: false` é DELIBERADO e não deve ser ligado sem
        // decisão explícita: ele anexa filtro de query e corpo de mutation.
        // Num app de dieta isso é dado de saúde indo pro Sentry.
        Sentry.supabaseIntegration({
          supabaseClient: supabase,
          sendOperationData: false,
        }),

        // Console → Sentry **Logs** (não Issues). É o oposto do
        // `captureConsoleIntegration`, que vira Issue e duplicaria cada crash
        // que o React já grita via console.error. Volume medido: 55 chamadas
        // de console no app inteiro, 48 delas `console.error`.
        Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),

        // Anexa propriedades extras de Error customizado (o SDK só manda
        // name/message/stack por padrão, e perde o resto).
        Sentry.extraErrorDataIntegration(),

        // ⛔ `httpClientIntegration` NÃO entra: não consegui confirmar quais
        // status ele captura por padrão nesta versão, e se pegar 4xx enche a
        // fila de Issues com fluxo normal (401 de token expirado, 404 do gate
        // V3 em usuário legado). Entra quando alguém medir o default.
      ],

      // Liga a store de **Logs** (top-level no v10; `_experiments.enableLogs`
      // está deprecated). Sem isto o `consoleLoggingIntegration` não entrega.
      enableLogs: true,

      // `window.onerror` e `unhandledrejection` já vêm ligados por padrão
      // (integração GlobalHandlers) — é o que faltava e não existia no app.
      //
      // NÃO adicionar CaptureConsole: o React loga erro capturado via
      // console.error, e isso duplicaria cada crash do ErrorBoundary.
      //
      // Session Replay ausente de propósito. Ver nota de LGPD acima.

      // 5% igual ao backend (SENTRY_TRACES_SAMPLE_RATE), pra não queimar quota.
      tracesSampleRate: 0.05,

      // Ruído conhecido que não é bug do app. `Load failed` fica FORA desta
      // lista de propósito — é justamente o sintoma que queremos enxergar.
      ignoreErrors: [
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
        /^Script error\.?$/,
      ],
      denyUrls: [/extensions\//i, /^chrome:\/\//i, /^safari-extension:\/\//i],

      beforeSend: scrubEvent,

      beforeSendSpan: (span) => {
        if (span.description) span.description = scrubTexto(span.description);
        if (span.data) span.data = scrubUnknown(span.data) as typeof span.data;
        return span;
      },

      // ⚠️ `beforeSend` NÃO roda em Log — são stores separadas, e sem este
      // segundo hook o console iria pro Sentry SEM passar pelo scrub de PII.
      // Foi a razão de eu quase não ligar o consoleLoggingIntegration.
      beforeSendLog: (log) => {
        if (typeof log.message === "string") log.message = scrubTexto(log.message);
        if (log.attributes) {
          for (const [k, v] of Object.entries(log.attributes)) {
            log.attributes[k] = scrubUnknown(v) as typeof v;
          }
        }
        return log;
      },
    });
  } catch {
    // Observabilidade nunca pode derrubar o app. Se o init falhar, o app
    // segue exatamente como antes — sem Sentry, mas de pé.
  }
}
